import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { FileWarning, Loader2, FileCheck2, ImageIcon } from "lucide-react";
import { api, API, formatMXN } from "@/lib/api";
import { INVOICE_STATUS } from "@/lib/constants";
import { toast } from "@/components/ui/sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function PendingInvoices() {
  const qc = useQueryClient();
  const [preview, setPreview] = useState(null);
  const { data = [], isLoading } = useQuery({
    queryKey: ["pending-invoices"],
    queryFn: async () => (await api.get("/pending-invoices")).data,
  });
  const ticketUrl = (m) => `${API}${(m.receipt_url || "").replace("/api", "")}`;

  const updateStatus = async (id, status) => {
    try {
      await api.post(`/movements/${id}/invoice-status`, { invoice_status: status });
      toast.success(status === "recibida" ? "Factura marcada como recibida" : "Estado actualizado");
      qc.invalidateQueries();
    } catch {
      toast.error("No se pudo actualizar el estado");
    }
  };

  return (
    <div className="py-4" data-testid="pending-invoices-page">
      <div className="flex items-center gap-2">
        <FileWarning className="w-6 h-6 text-amber-500" />
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Pendientes por facturar</h1>
      </div>
      <p className="text-muted-foreground text-sm mt-1 mb-8">Movimientos «To» marcados como «requiere factura» sin factura aún.</p>

      {isLoading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
      ) : data.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center">
          <FileCheck2 className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">¡Todo facturado! No hay movimientos pendientes por facturar.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="pending-invoices-list">
          {data.map((m, i) => {
            const st = INVOICE_STATUS[m.invoice_status] || INVOICE_STATUS.pendiente;
            return (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="rounded-2xl border border-border bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                  <FileWarning className="w-5 h-5 text-amber-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{m.category}</p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-numeric">{m.movement_date}</span>{m.responsible ? ` · ${m.responsible}` : ""}
                  </p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
                <span className="font-numeric font-bold text-foreground sm:w-32 sm:text-right">{formatMXN(m.amount)}</span>
                {m.receipt_url && (
                  <button data-testid={`view-ticket-${m.id}`} onClick={() => setPreview(ticketUrl(m))} title="Ver ticket adjunto"
                    className="w-9 h-9 rounded-xl bg-secondary/60 hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-emerald-500 shrink-0 transition-colors">
                    <ImageIcon className="w-4 h-4" />
                  </button>
                )}
                <Select value={m.invoice_status} onValueChange={(v) => updateStatus(m.id, v)}>
                  <SelectTrigger data-testid={`invoice-status-${m.id}`} className="rounded-xl w-40 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(INVOICE_STATUS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </motion.div>
            );
          })}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg" data-testid="ticket-preview-dialog">
          <DialogHeader><DialogTitle>Ticket adjunto</DialogTitle></DialogHeader>
          {preview && <img src={preview} alt="Ticket adjunto" data-testid="ticket-preview-image" className="w-full max-h-[70vh] object-contain rounded-xl border border-border bg-black/20" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
